/**
 * What a user-agent string actually says, and nothing more.
 *
 * The design draws "Chrome · macOS" beside a session. identity.refresh_token
 * and identity.device_trust store the raw `User-Agent` header and nothing else
 * — no parsed device, no geo, no "Makati office". Two ways to honour the design
 * were available and only one of them is honest: parse the string that IS
 * recorded, locally, and show what was parsed BESIDE what it was parsed from —
 * or call an enrichment service that does not exist and never will, because
 * sending an operator's browser fingerprint to a third party to make a label
 * prettier is not a trade this platform makes.
 *
 * So this is a small, deliberately dumb matcher over the tokens browsers
 * actually send, and it says `null` rather than guessing when it does not
 * recognise something. `describe()` returns the raw string in that case, so the
 * screen always shows a fact.
 *
 * WHY THE ORDER MATTERS. Every one of these strings contains "Mozilla/5.0", and
 * Edge contains "Chrome" which contains "Safari". Matching in declaration order
 * from most specific to least is the whole of the correctness here — a matcher
 * that checked Safari first would report every Chrome on the platform as
 * Safari, which is worse than saying nothing.
 */

interface Rule {
  readonly name: string;
  readonly test: RegExp;
}

/**
 * Most specific first, and the order IS the correctness.
 *
 * The non-human agents lead, because a headless browser's string ends
 * "…HeadlessChrome/131.0.0.0 Safari/537.36" — it does not contain a word-
 * boundaried "Chrome/", so listing it below the browsers made every Playwright
 * session on the platform report as Safari. That is precisely the row a
 * security lead is scanning for, wearing the label of an ordinary desktop.
 *
 * Then Edge and Opera, which both carry "Chrome/", then Chrome, which carries
 * "Safari/". Reversing any pair here produces a plausible wrong answer rather
 * than an obvious one.
 */
const BROWSERS: readonly Rule[] = [
  { name: 'curl', test: /^curl\// },
  { name: 'Playwright', test: /\bHeadlessChrome\/|\bPlaywright\b/ },
  { name: 'a script', test: /\bpython-requests\/|\bGo-http-client\/|\bnode-fetch\b|\baxios\// },
  { name: 'Edge', test: /\bEdgA?\/|\bEdg\// },
  { name: 'Opera', test: /\bOPR\/|\bOpera\// },
  { name: 'Samsung Internet', test: /\bSamsungBrowser\// },
  { name: 'Firefox', test: /\bFirefox\/|\bFxiOS\// },
  { name: 'Chrome', test: /\bChrome\/|\bCriOS\// },
  { name: 'Safari', test: /\bSafari\// },
];

/** iOS before macOS: an iPhone's agent contains "like Mac OS X". */
const PLATFORMS: readonly Rule[] = [
  { name: 'iPhone', test: /\biPhone\b/ },
  { name: 'iPad', test: /\biPad\b/ },
  { name: 'Android', test: /\bAndroid\b/ },
  { name: 'Windows', test: /\bWindows NT\b/ },
  { name: 'macOS', test: /\bMac OS X\b|\bMacintosh\b/ },
  { name: 'ChromeOS', test: /\bCrOS\b/ },
  { name: 'Linux', test: /\bLinux\b/ },
];

export interface ParsedAgent {
  /** Null when nothing in the list matched — never a guess. */
  browser: string | null;
  platform: string | null;
  /** Exactly what was stored. Always shown, so the parse can be checked. */
  raw: string;
}

export function parseUserAgent(raw: string): ParsedAgent {
  const value = raw.trim();
  return {
    browser: BROWSERS.find((b) => b.test.test(value))?.name ?? null,
    platform: PLATFORMS.find((p) => p.test.test(value))?.name ?? null,
    raw: value,
  };
}

/**
 * One line for a session row.
 *
 * Falls back to the RAW STRING rather than to "Unknown device": an unrecognised
 * agent is still evidence, and replacing it with a shrug throws away the only
 * thing that would tell an investigator what connected.
 */
export function describeUserAgent(raw: string | null | undefined): string {
  if (!raw || raw.trim() === '') return 'No user agent recorded';
  const parsed = parseUserAgent(raw);
  if (parsed.browser && parsed.platform) return `${parsed.browser} · ${parsed.platform}`;
  if (parsed.browser) return parsed.browser;
  if (parsed.platform) return parsed.platform;
  /* Long agents are truncated for the summary line only — the full string is
     rendered underneath it, so nothing is lost. */
  return parsed.raw.length > 60 ? `${parsed.raw.slice(0, 57)}…` : parsed.raw;
}
